/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import type { ContextType, JSX, Ref } from 'preact';
import { forwardRef } from 'preact/compat';
import { useCallback } from 'preact/hooks';

import {
  DefaultList,
  DropdownList,
  EmptyResults,
  type SelectSingleCollectionRendererContext
} from '#PRIVATE_SelectCommon';
import { Flex } from '#UNSAFE_Flex';
import { Label } from '#UNSAFE_Label';
import { SelectSingleField } from '#UNSAFE_SelectSingle/SelectSingleField';
import { SelectSingleFieldInput } from '#UNSAFE_SelectSingle/SelectSingleFieldInput';
import { Separator } from '#UNSAFE_Separator';
import { InlineUserAssistance } from '#UNSAFE_UserAssistance';
import { View } from '#UNSAFE_View';
import { FormFieldContext } from '#hooks/UNSAFE_useFormFieldContext';
import { TabbableModeContext } from '#hooks/UNSAFE_useTabbableMode';
import type { Data, InlineSelectHandle, InlineSelectSingleProps, Key } from './types';
import { useInlineSelectSingle } from './useInlineSelectSingle';

const PREVENT_TABBING = { isTabbable: false } satisfies ContextType<typeof TabbableModeContext>;

/**
 * A InlineSelectSingle is a text field with an inline list that supports single selection and search filtering.
 * Note: This is only meant to be used by the Spectra Smart Search component. As such, it only has limited features
 * needed for the Smart Search component.
 */
function InlineSelectSingleImpl<K extends Key, D extends Data>(
  props: InlineSelectSingleProps<K, D>,
  ref: Ref<InlineSelectHandle>
) {
  const {
    data,
    itemRenderer,
    itemText,
    label,
    onLoadRange,
    testId,
    userAssistancePosition = 'belowList'
  } = props;
  const {
    collectionRendererContext,
    formFieldContext,
    hasUserAssistance,
    isEmptyResults,
    isUserFiltering,
    labelProps,
    listContainerProps,
    selectSingleFieldProps,
    selectFieldInputProps,
    userAssistanceProps
  } = useInlineSelectSingle(props, ref);

  const defaultCollectionRenderer = useCallback(
    ({
      onPersistCurrentRowKey,
      onRowAction,
      selected,
      searchText,
      currentRowKeyOverride
    }: SelectSingleCollectionRendererContext<K, D>) => (
      <DefaultList
        aria-label={label}
        componentName="InlineSelectSingle"
        currentItemVariant="highlight"
        currentKey={currentRowKeyOverride}
        data={data}
        itemRenderer={itemRenderer}
        itemText={itemText}
        onCurrentKeyChange={onPersistCurrentRowKey}
        onItemAction={onRowAction}
        onLoadRange={onLoadRange}
        searchText={isUserFiltering ? searchText : undefined}
        selectedKeys={selected}
        selectionMode="single"
      />
    ),
    [data, isUserFiltering, itemRenderer, itemText, label, onLoadRange]
  );

  const userAssistance = (
    <View minWidth="100%" padding="2x">
      <InlineUserAssistance
        testId={testId && `${testId}_userAssistance`}
        {...userAssistanceProps}
      />
    </View>
  );

  return (
    <Flex direction="column" gap="2x" maxHeight="100%" width="100%">
      <FormFieldContext.Provider value={formFieldContext}>
        <View flex={0}>
          <SelectSingleField
            {...selectSingleFieldProps}
            label={<Label {...labelProps} />}
            testId={testId && `${testId}_selectSingleField`}>
            <SelectSingleFieldInput {...selectFieldInputProps} />
          </SelectSingleField>
          {/* Render separator & UA without gaps when we are configured for UA between the field and the dropdown */}
          {userAssistancePosition === 'aboveList' && <Separator />}
          {userAssistancePosition === 'aboveList' && hasUserAssistance && (
            <>
              {userAssistance}
              <Separator />
            </>
          )}
        </View>
        {/* Render separator with gaps when we are not configured for UA between the field and the dropdown */}
        {userAssistancePosition !== 'aboveList' && <Separator />}
        <DropdownList
          {...listContainerProps}
          // Add bottom gap only when we are configured to show UA above the list
          hasBottomGap={userAssistancePosition === 'aboveList'}
          hasTopGap={false}>
          <TabbableModeContext.Provider value={PREVENT_TABBING}>
            {isEmptyResults ? (
              <EmptyResults />
            ) : (
              defaultCollectionRenderer(collectionRendererContext)
            )}
          </TabbableModeContext.Provider>
        </DropdownList>
        {userAssistancePosition === 'belowList' && hasUserAssistance && (
          <div>
            <Separator />
            {userAssistance}
          </div>
        )}
      </FormFieldContext.Provider>
    </Flex>
  );
}

export const InlineSelectSingle = forwardRef(InlineSelectSingleImpl) as {
  <K extends Key, D extends Data>(
    props: InlineSelectSingleProps<K, D> & { ref?: Ref<InlineSelectHandle> }
  ): JSX.Element;
}; // forwardRef will remove generics, so we cast it explicitly to preserve generics.;
